/**
 * Math macro collection: LyX FormulaMacro insets, preamble \newcommand/\def & co,
 * and \input{...}ed macro files. The result feeds the math editor's macro dictionary so that
 * user macros render instantly in the WYSIWYG view.
 */
import type { LyxDocument } from './lyx/ast.ts';
import { getPreamble, walkInsets, unquote, paramMap } from './lyx/ast.ts';

export interface MacroDef {
  name: string;          // without backslash
  args: number;
  def: string;           // LaTeX definition body (#1..#n)
  optional?: string;     // default value of the optional first argument, if any
  display?: string;      // LyX "display" form (what LyX shows instead of the expansion)
  conditional?: boolean; // value of a declared \newif switch; omitted from the renderer's macros
  source: 'lyx' | 'preamble' | 'file';
  raw: string;           // the original definition text
}

/** Read a balanced {...} group starting at s[i] === '{'. Returns [content, indexAfter]. */
export function readGroup(s: string, i: number): [string, number] | null {
  if (s[i] !== '{') return null;
  let depth = 0;
  for (let j = i; j < s.length; j++) {
    const c = s[j];
    if (c === '\\') { j++; continue; }
    if (c === '{') depth++;
    else if (c === '}') { depth--; if (depth === 0) return [s.slice(i + 1, j), j + 1]; }
  }
  return null;
}

export function readBracket(s: string, i: number): [string, number] | null {
  if (s[i] !== '[') return null;
  let depth = 0;
  for (let j = i; j < s.length; j++) {
    const c = s[j];
    if (c === '\\') { j++; continue; }
    if (c === '{') depth++;
    else if (c === '}') depth--;
    else if (c === ']' && depth === 0) return [s.slice(i + 1, j), j + 1];
  }
  return null;
}

function skipWs(s: string, i: number): number {
  while (i < s.length && /\s/.test(s[i])) i++;
  return i;
}

/** Read a control sequence name at s[i] (which may be '{\name}' or '\name'). */
function readCsName(s: string, i: number): [string, number] | null {
  i = skipWs(s, i);
  if (s[i] === '{') {
    const g = readGroup(s, i);
    if (!g) return null;
    const m = /^\s*\\([A-Za-z@]+|.)\s*$/.exec(g[0]);
    return m ? [m[1], g[1]] : null;
  }
  if (s[i] === '\\') {
    const m = /^\\([A-Za-z@]+|.)/.exec(s.slice(i));
    if (!m) return null;
    return [m[1], i + m[0].length];
  }
  return null;
}

/**
 * Parse one macro definition starting at position `i` in `s` (s[i] should be the backslash of
 * \newcommand, \renewcommand, \providecommand, \def, \global\long\def, \DeclareMathOperator,
 * \DeclarePairedDelimiter, \NewDocumentCommand, \DeclareRobustCommand, \let). Returns the macro and
 * the index after the definition, or null if this is not a definition.
 */
export function parseMacroAt(s: string, i: number, source: MacroDef['source']): [MacroDef, number] | null {
  const head = /^(?:\\global\s*)?(?:\\long\s*)?(?:\\outer\s*)?(?:\\protected\s*)?\\(newcommand\*?|renewcommand\*?|providecommand\*?|DeclareRobustCommand\*?|NewDocumentCommand|RenewDocumentCommand|DeclareDocumentCommand|ProvideDocumentCommand|DeclareMathOperator\*?|DeclarePairedDelimiter(?:X|XPP)?|def|edef|gdef|xdef|let)(?![A-Za-z])/.exec(s.slice(i));
  if (!head) return null;
  const kind = head[1];
  let j = i + head[0].length;
  const nameRes = readCsName(s, j);
  if (!nameRes) return null;
  const [name, afterName] = nameRes;
  j = afterName;
  const rawStart = i;

  if (kind === 'let') {
    j = skipWs(s, j);
    if (s[j] === '=') j++;
    const t = readCsName(s, j);
    if (!t) return null;
    return [{ name, args: 0, def: '\\' + t[0], source, raw: s.slice(rawStart, t[1]) }, t[1]];
  }
  if (kind === 'def' || kind === 'edef' || kind === 'gdef' || kind === 'xdef') {
    // parameter text: #1#2... (possibly with delimiters, which we ignore)
    let args = 0;
    while (j < s.length && s[j] !== '{') {
      if (s[j] === '#' && /\d/.test(s[j + 1] ?? '')) { args = Math.max(args, Number(s[j + 1])); j += 2; }
      else j++;
    }
    const g = readGroup(s, j);
    if (!g) return null;
    return [{ name, args, def: g[0].trim(), source, raw: s.slice(rawStart, g[1]) }, g[1]];
  }
  if (kind.startsWith('DeclareMathOperator')) {
    j = skipWs(s, j);
    const g = readGroup(s, j);
    if (!g) return null;
    const star = kind.endsWith('*') ? '*' : '';
    return [{ name, args: 0, def: `\\operatorname${star}{${g[0]}}`, source, raw: s.slice(rawStart, g[1]) }, g[1]];
  }
  if (kind.startsWith('DeclarePairedDelimiter')) {
    // \DeclarePairedDelimiter{\abs}{\lvert}{\rvert}  (X/XPP variants have extra args; approximate)
    j = skipWs(s, j);
    let nargs = 1;
    if (kind === 'DeclarePairedDelimiterX' || kind === 'DeclarePairedDelimiterXPP') {
      const b = readBracket(s, j);
      if (b) { nargs = Number(b[0]) || 1; j = b[1]; }
    }
    const groups: string[] = [];
    let pos = skipWs(s, j);
    while (groups.length < (kind === 'DeclarePairedDelimiterXPP' ? 5 : kind === 'DeclarePairedDelimiterX' ? 3 : 2)) {
      const g = readGroup(s, pos);
      if (!g) break;
      groups.push(g[0]);
      pos = skipWs(s, g[1]);
    }
    if (groups.length < 2) return null;
    let l: string, r: string, body: string;
    if (kind === 'DeclarePairedDelimiterXPP') { l = groups[1]; r = groups[2]; body = groups[4] ?? '#1'; }
    else if (kind === 'DeclarePairedDelimiterX') { l = groups[0]; r = groups[1]; body = groups[2] ?? '#1'; }
    else { l = groups[0]; r = groups[1]; body = '#1'; }
    return [{ name, args: nargs, def: `\\left${l}${body}\\right${r}`, source, raw: s.slice(rawStart, pos) }, pos];
  }
  if (kind.endsWith('DocumentCommand')) {
    j = skipWs(s, j);
    const spec = readGroup(s, j);
    if (!spec) return null;
    const specStr = spec[0].replace(/\s/g, '');
    const args = (specStr.match(/[mOodvbr]/gi) ?? []).length;
    const g = readGroup(s, skipWs(s, spec[1]));
    if (!g) return null;
    return [{ name, args, def: g[0].trim(), source, raw: s.slice(rawStart, g[1]) }, g[1]];
  }
  // \newcommand-like
  j = skipWs(s, j);
  let args = 0;
  let optional: string | undefined;
  const b1 = readBracket(s, j);
  if (b1) {
    args = Number(b1[0].trim()) || 0;
    j = skipWs(s, b1[1]);
    const b2 = readBracket(s, j);
    if (b2) { optional = b2[0]; j = skipWs(s, b2[1]); }
  }
  let def: string;
  let end: number;
  if (s[j] === '{') {
    const g = readGroup(s, j);
    if (!g) return null;
    def = g[0]; end = g[1];
  } else {
    // single-token body like \newcommand\foo\bar
    const m = /^\\[A-Za-z@]+|^./.exec(s.slice(j));
    if (!m) return null;
    def = m[0]; end = j + m[0].length;
  }
  const md: MacroDef = { name, args, def: def.trim(), source, raw: s.slice(rawStart, end) };
  if (optional !== undefined) md.optional = optional;
  return [md, end];
}

/** Strip LaTeX comments (unescaped % to end of line). */
export function stripComments(s: string): string {
  return s.split('\n').map(line => {
    let out = '';
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '\\') { out += c + (line[i + 1] ?? ''); i++; continue; }
      if (c === '%') break;
      out += c;
    }
    return out;
  }).join('\n');
}

/**
 * Names the math editor implements itself: a preamble that wraps them (OverLyX's own \llangle
 * snippet redefines \left and \right, see math/llangle.ts) must not turn them into user macros —
 * `\left[` would then render as the word "left" followed by a bracket.
 */
const STRUCTURAL = new Set(['left', 'right', 'begin', 'end']);

/** Visit definitions, switches and input files at their positions in the source. */
function scanMacros(text: string, source: MacroDef['source'], add: (macro: MacroDef) => void, input: (name: string) => void, conditionals: Set<string>): void {
  // "\global\long\def\inv#1{\myinv{(#1)}}%% @display {(#1)^{-1}}": the display form OverLyX writes
  // for a LyX macro (latexMacro) — kept for the editor also when the file is read as plain text
  const displays = new Map<string, string>();
  for (const line of text.split('\n')) {
    const dm = /%% @display (\{.*\})\s*$/.exec(line);
    if (!dm) continue;
    const nm = /\\(?:(?:global|long|protected)\s*\\)*(?:[gex]?def)\s*\\([A-Za-z@]+)|\\(?:re|provide)?newcommand\*?\s*\{?\s*\\([A-Za-z@]+)/.exec(line.slice(0, dm.index));
    const g = readGroup(dm[1], 0);
    if (nm && g && g[0].trim()) displays.set(nm[1] ?? nm[2], g[0].trim());
  }
  const s = stripComments(text);
  for (let i = 0; i < s.length; i++) {
    if (s[i] !== '\\') continue;
    const inp = /^\\(?:input|include)\s*\{([^}]+)\}/.exec(s.slice(i));
    if (inp) { input(inp[1].trim()); i += inp[0].length - 1; continue; }
    const cmd = readCsName(s, i);
    if (!cmd) continue;
    if (cmd[0] === 'newif') {
      const name = readCsName(s, cmd[1]);
      if (name && /^if[A-Za-z@]+$/.test(name[0])) {
        conditionals.add(name[0]);
        add({ name: name[0], args: 0, def: '', conditional: false, source, raw: s.slice(i, name[1]) });
        i = name[1] - 1;
        continue;
      }
    }
    const assignment = /^(.+)(true|false)$/.exec(cmd[0]);
    if (assignment && conditionals.has('if' + assignment[1])) {
      add({ name: 'if' + assignment[1], args: 0, def: '', conditional: assignment[2] === 'true', source, raw: s.slice(i, cmd[1]) });
      i = cmd[1] - 1;
      continue;
    }
    const res = parseMacroAt(s, i, source);
    if (res) {
      const d = displays.get(res[0].name);
      if (d !== undefined && res[0].display === undefined) res[0].display = d;
      if (!STRUCTURAL.has(res[0].name)) add(res[0]);
      i = res[1] - 1;
    } else i = cmd[1] - 1;
  }
}

/** Scan LaTeX source for macro definitions. Also returns \input/\include file references. */
export function macrosFromLatex(text: string, source: MacroDef['source'] = 'preamble'): { macros: MacroDef[]; inputs: string[] } {
  const macros: MacroDef[] = [], inputs: string[] = [];
  scanMacros(text, source, m => macros.push(m), name => inputs.push(name), new Set());
  return { macros, inputs };
}

/** Parse a LyX FormulaMacro inset (lines[0] definition, optional lines[1] "{display}"). */
export function macroFromLyxLines(lines: string[]): MacroDef | null {
  if (!lines.length) return null;
  const res = parseMacroAt(lines[0].trim(), 0, 'lyx');
  if (!res) return null;
  const md = res[0];
  if (lines[1] && lines[1].startsWith('{')) {
    const g = readGroup(lines[1], 0);
    if (g && g[0].trim()) md.display = g[0].trim();
  }
  return md;
}

export interface MacroResolver {
  /** resolve a LyX child document by its filename (relative to the document) */
  include?: (filename: string) => LyxDocument | undefined;
  /** read a text file (for \input{macros} in the preamble), relative to the document */
  readFile?: (filename: string) => string | undefined;
}

export interface CollectMacrosOptions {
  /** Child documents inherit the master's parsed header; do not collect that inherited preamble twice. */
  includePreamble?: boolean;
  /** files read first, as if \input at the top of the preamble (an HTML article's `<link rel="overlyx-macros">`) */
  files?: string[];
}

/**
 * Collect all macros visible in a document: preamble (+ \input files, recursively),
 * FormulaMacro insets in the body, and included child documents (in document order).
 * Later definitions override earlier ones (like LaTeX).
 */
export function collectMacros(doc: LyxDocument, resolver: MacroResolver = {}, options: CollectMacrosOptions = {}, seen = new Set<string>()): MacroDef[] {
  const out: MacroDef[] = [];
  const conditionals = new Set<string>();
  const visitLatex = (text: string, source: MacroDef['source']) => scanMacros(text, source, m => out.push(m), visitFile, conditionals);
  const visitFile = (name: string) => {
    const candidates = name.endsWith('.tex') ? [name] : [name + '.tex', name];
    for (const c of candidates) {
      if (seen.has('file:' + c)) return;
      const txt = resolver.readFile?.(c);
      if (txt === undefined) continue;
      seen.add('file:' + c);
      visitLatex(txt, 'file');
      // Guard recursion, while allowing a later \input to execute the file again.
      seen.delete('file:' + c);
      return;
    }
  };
  for (const f of options.files ?? []) visitFile(f);
  const visitDoc = (current: LyxDocument, includePreamble: boolean) => {
    if (includePreamble) visitLatex(getPreamble(current), 'preamble');
    for (const { inset } of walkInsets(current.body)) {
      if (inset.type === 'FormulaMacro') {
        const md = macroFromLyxLines(inset.lines);
        if (md) out.push(md);
      } else if (inset.type === 'Leaf' && inset.name === 'CommandInset' && inset.arg === 'include') {
        const pm = paramMap(inset.params);
        const fn = unquote(pm.get('filename'));
        if (fn && !seen.has('lyx:' + fn)) {
          seen.add('lyx:' + fn);
          // a child document (.lyx or .tex) is read as a document: its macros keep their positions
          // (also the ones inside notes, which LyX evaluates); other files are scanned as text
          const child = fn.endsWith('.lyx') || fn.endsWith('.tex') || !fn.includes('.') ? resolver.include?.(fn) : undefined;
          // Fragment parsers attach the master's header to children for layout resolution. Its
          // preamble has already been collected above and must not override later macro-file defs.
          if (child) visitDoc(child, false);
          else if (!fn.endsWith('.lyx')) visitFile(fn);
        }
      }
    }
  };
  visitDoc(doc, options.includePreamble !== false);
  return out;
}

// Track unsupported primitive conditionals too, so their \else/\fi cannot close a known switch.
// Math commands such as \iff are not conditional openers.
const PRIMITIVE_CONDITIONALS = new Set(['if', 'ifcat', 'ifnum', 'ifdim', 'ifodd', 'ifvmode', 'ifhmode', 'ifmmode', 'ifinner', 'ifvoid', 'ifhbox', 'ifvbox', 'ifx', 'ifeof', 'iftrue', 'iffalse', 'ifcase', 'ifdefined', 'ifcsname', 'iffontchar', 'ifincsname']);

/** Resolve declared boolean switches at render time, keeping unknown or incomplete TeX intact. */
function resolveConditionals(def: string, values: Map<string, boolean>): string {
  const out: string[] = [];
  const stack: { name: string; open: string; yes: string[]; no?: string[] }[] = [];
  const append = (text: string) => {
    const frame = stack[stack.length - 1];
    (frame ? frame.no ?? frame.yes : out).push(text);
  };
  let last = 0;
  for (const token of def.matchAll(/\\([A-Za-z@]+|[\s\S])/g)) {
    append(def.slice(last, token.index));
    const name = token[1], frame = stack[stack.length - 1];
    if (values.has(name) || PRIMITIVE_CONDITIONALS.has(name)) {
      stack.push({ name, open: token[0], yes: [] });
    } else if (name === 'else' && frame && frame.no === undefined) {
      frame.no = [];
    } else if (name === 'fi' && frame) {
      stack.pop();
      const value = values.get(frame.name);
      append(value === undefined ? frame.open + frame.yes.join('') + (frame.no ? '\\else' + frame.no.join('') : '') + token[0]
        : (value ? frame.yes : frame.no ?? []).join(''));
    } else append(token[0]);
    last = token.index + token[0].length;
  }
  append(def.slice(last));
  while (stack.length) {
    const frame = stack.pop()!;
    append(frame.open + frame.yes.join('') + (frame.no ? '\\else' + frame.no.join('') : ''));
  }
  return out.join('');
}

/** Convert macro definitions to the math editor's metadata format (legacy function name). */
export function toMathliveMacros(macros: MacroDef[]): Record<string, { def: string; args: number; optional?: string; expand: boolean }> {
  const out: ReturnType<typeof toMathliveMacros> = {};
  // all definitions, also the internal \@helpers that \mathpalette calls (the last one wins)
  const byName = new Map(macros.map(m => [m.name, m]));
  const conditionals = new Map<string, boolean>([['iftrue', true], ['iffalse', false]]);
  for (const m of byName.values()) if (m.conditional !== undefined) conditionals.set(m.name, m.conditional);
  for (const m of byName.values()) {
    if (!/^[A-Za-z]+$/.test(m.name) || m.conditional !== undefined) continue;
    const resolved = resolveConditionals(m.display ?? expandMathpalette(m.def, byName), conditionals);
    const def = m.display !== undefined ? resolved : sanitizeForMathlive(resolved, m);
    out[m.name] = { def, args: m.args, expand: false,
      ...(m.optional !== undefined && m.args > 0 ? { optional: resolveConditionals(m.optional, conditionals) } : {}) };
  }
  return out;
}

/** Rewrite each occurrence and its nested arguments once, even when `fn` retains the command. */
export function replaceCommand(def: string, cmd: string, fn: (content: string, opts: string[]) => string, nGroups = 1): string {
  let out = def, cursor = 0;
  for (;;) {
    const i = out.indexOf('\\' + cmd, cursor);
    if (i < 0) break;
    // the command name must end here (not a prefix of a longer command)
    const after = out[i + cmd.length + 1];
    if (after && /[A-Za-z]/.test(after)) { cursor = i + cmd.length + 1; continue; }
    let j = skipWs(out, i + cmd.length + 1);
    const opts: string[] = [];
    let groups: string[] = [];
    for (let g = 0; g < nGroups; g++) {
      // [options] and {mandatory} groups in any order before the last mandatory group
      for (;;) {
        j = skipWs(out, j);
        if (out[j] === '[') { const b = readBracket(out, j); if (!b) break; opts.push(b[0]); j = b[1]; continue; }
        break;
      }
      const grp = readGroup(out, j);
      if (!grp) { groups = []; break; }
      groups.push(grp[0]); j = grp[1];
    }
    if (!groups.length) { out = out.slice(0, i) + '\\mathrm{' + cmd + '}' + out.slice(i + cmd.length + 1); continue; }
    groups = groups.map(group => replaceCommand(group, cmd, fn, nGroups));
    const replacement = fn(groups[groups.length - 1], [...opts, ...groups.slice(0, -1)]);
    out = out.slice(0, i) + replacement + out.slice(j);
    cursor = i + replacement.length;
  }
  return out;
}

/** Strip a text-mode `$...$` wrapper (math inside \raisebox / \scalebox content). */
function unmath(s: string): string {
  const t = s.trim();
  const m = /^\$([\s\S]*)\$$/.exec(t);
  return m ? m[1] : t;
}

/**
 * plain TeX's \mathpalette\helper{arg}: the helper with each math style as #1, picked by \mathchoice.
 * Helpers that are not known two-argument macros stay as they are.
 */
export function expandMathpalette(def: string, macros: Map<string, MacroDef>): string {
  let out = def;
  for (let i = out.indexOf('\\mathpalette'); i >= 0; i = out.indexOf('\\mathpalette', i + 1)) {
    const name = readCsName(out, i + '\\mathpalette'.length);
    const helper = name && macros.get(name[0]);
    if (!name || !helper || helper.args !== 2) continue;
    const arg = readGroup(out, skipWs(out, name[1]));
    if (!arg) continue;
    const branch = (style: string) => `{${helper.def.replace(/#1/g, style).replace(/#2/g, arg[0])}}`;
    out = out.slice(0, i) + '\\mathchoice' + ['\\displaystyle', '\\textstyle', '\\scriptstyle', '\\scriptscriptstyle'].map(branch).join('') + out.slice(arg[1]);
  }
  return out;
}

/** Keep explicit baseline shifts, also by the content's \height or \depth; \raisebox takes its math inside its text box. */
export function approximateRaisebox(content: string, args: string[]): string {
  const lift = args[args.length - 1].trim(), body = unmath(content);
  return /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)\s*[a-z]{2}$/.test(lift) || /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)?\s*\\(?:height|depth)$/.test(lift)
    ? `\\raisebox{${lift}}{$${body}$}` : `{${body}}`;
}

/** Browser-math equivalent of the common \ooalign + fractional glyph-width overlay idiom. */
export function approximateOverlapSymbols(offset: string, args: string[]): string {
  const left = args[0] ?? '', right = args[1] ?? '';
  const widths: Record<string, number> = { '\\Phi': 0.764, '\\mathrm{H}': 0.75, '\\mathrm{G}': 0.786, '\\mathrm{X}': 0.75, '\\mathrm{Y}': 0.75, '\\Delta': 0.833 };
  const fraction = Number.parseFloat(offset.trim());
  const shift = Number.isFinite(fraction) ? Math.max(0, Math.min(1, fraction)) : 0.5;
  const overlap = Number(((1 - shift) * (widths[left.replace(/\s/g, '')] ?? 0.75)).toFixed(4));
  return `\\mathord{${left}\\kern-${overlap}em${right}}`;
}

/** Image symbols inherit text colour; font-measured images follow the math style. */
export function approximateImageSymbols(def: string): string {
  if (!/\\include(?:graphics|svg)\b/.test(def)) return def;
  const heights = new Map<string, number>();
  // Computer Modern: a digit is 0.64444em tall, a capital 0.68333em.
  // A font-character height is not an em (which was making these symbols ~55% too tall).
  let d = def.replace(/\\([A-Za-z@]+)\s*=\s*\\fontcharht\s*\\font\s*`([0-9A-Z])/g, (_all, register: string, glyph: string) => {
    heights.set(register, /[0-9]/.test(glyph) ? 0.64444 : 0.68333);
    return '';
  });
  d = d.replace(/\\includesvg\b/g, '\\includegraphics');
  const alreadyMarked = d.includes('\\htmlClass{lm-image-glyph}');
  d = d.replace(/\\includegraphics\s*(?:\[([^\]]*)\])?\s*\{([^{}]*)\}/g, (_all, options: string = '', file: string) => {
    const measured = /(?:height|width|totalheight)\s*=\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)?\\([A-Za-z@]+)/g;
    const fontSized = [...options.matchAll(measured)].some(m => heights.has(m[1]));
    const image = (scale: number) => {
      const sizes = options.replace(/((?:height|width|totalheight)\s*=\s*)([+-]?(?:\d+(?:\.\d*)?|\.\d+)?)\\([A-Za-z@]+)/g, (all, key: string, factor: string, register: string) => {
        const height = heights.get(register);
        return height === undefined ? all : key + Number(((factor ? Number(factor) : 1) * height * scale).toFixed(5)) + 'em';
      });
      const body = `\\includegraphics${sizes ? `[${sizes}]` : ''}{${file}}`;
      return (/\.svgz?\s*$/i.test(file) || /^\s*\\mathord\b/.test(def)) && !alreadyMarked ? `\\htmlClass{lm-image-glyph}{${body}}` : body;
    };
    // TeX's em lengths refer to textstyle even in scripts (so do OverLyX's \includegraphics). Account for the 0.7/0.5
    // script scales so the image and its layout box shrink together with nearby letters.
    return fontSized ? `\\mathchoice{${image(1)}}{${image(1)}}{${image(0.7)}}{${image(0.5)}}` : image(1);
  });
  return d;
}

/**
 * MathLive cannot render some TeX internals / text-mode constructs used in macro definitions.
 * Rewrite the common ones into a visual approximation, and fall back to the macro name
 * (keeping the arguments visible) for the rest.
 */
export function sanitizeForMathlive(def: string, m: { name: string; args: number }): string {
  // \m@th (\mathsurround=0pt) changes nothing in a browser
  let d = def.replace(/\r?\n\s*/g, ' ').replace(/\\m@th(?![A-Za-z@])/g, '');
  // text-mode boxes that only change size / position: keep the content
  d = replaceCommand(d, 'scalebox', c => `{${unmath(c)}}`, 2);
  d = replaceCommand(d, 'resizebox', c => `{${unmath(c)}}`, 3);
  d = replaceCommand(d, 'rotatebox', c => `{${unmath(c)}}`, 2);
  d = replaceCommand(d, 'raisebox', approximateRaisebox, 2);
  d = replaceCommand(d, 'vcenter', c => `{${c}}`);
  d = replaceCommand(d, 'vbox', c => `{${c}}`);
  d = replaceCommand(d, 'hbox', c => `\\text{${c}}`);
  d = replaceCommand(d, 'ensuremath', c => `{${c}}`);
  d = replaceCommand(d, 'textnormal', c => `\\text{${c}}`);
  d = replaceCommand(d, 'accentset', (c, o) => `\\overset{${o[0] ?? ''}}{${c}}`, 2);
  // Approximate the paper's \\sbox/\\ooalign double-glyph helper with a supported inline overlay.
  d = replaceCommand(d, 'OverlapSymbols', approximateOverlapSymbols, 3);
  // OverLyX's MathJax has \includegraphics. Treat \includesvg the same way; the client sends both
  // through the project's graphics endpoint, which converts PDF/SVG to a browser image.
  d = approximateImageSymbols(d);
  // \mathchoice with its four arguments as adjacent groups
  d = replaceCommand(d, 'mathchoice', (last, args) => '\\mathchoice' + [...args, last].map(value => `{${value}}`).join(''), 4);
  if (/\\includegraphics\b/.test(d)) {
    d = d.replace(/\\normalfont\b/g, '');
    // \includegraphics is a math-mode command in OverLyX's MathJax, even when the LaTeX definition used a
    // text/raise box only to establish its baseline.
    d = replaceCommand(d, 'text', c => /\\includegraphics\b/.test(c) ? `{${c}}` : `\\text{${c}}`);
  }
  d = d.replace(/\\relax\b/g, '');
  // things we cannot approximate: show the macro name with its arguments
  if (/\\(includesvg|sbox|usebox|ooalign|mathpalette|fontcharht|fontdimen|csname|expandafter|noexpand|@)/.test(d)) {
    let f = `\\mathrm{${m.name}}`;
    for (let k = 1; k <= m.args; k++) f += `\\{#${k}\\}`;
    return f;
  }
  return d;
}
